import { useState } from 'react';
import type { Environment, Project } from '@hangar/shared';
import { toast } from 'sonner';
import { useEnvironmentMutations } from '@/api/hooks';
import { showMutationError } from '@/lib/mutationError';
import { ConfirmArchiveDialog } from '@/components/ConfirmArchiveDialog';
import { Icon } from '@/components/Icon';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { blockerLabel, blockerSample, blockersOf, type Blocker } from './environmentHelpers';
import { environmentEtag } from './environmentsCardModel';

interface Props {
  project: Project;
  env: Environment | null;
  onOpenChange: (open: boolean) => void;
}

/** Archive request: a 409 keeps the dialog open and lists what still uses the environment; closing clears the list. */
function useArchiveEnvironment(project: Project, env: Environment | null, onOpenChange: (open: boolean) => void) {
  const { update } = useEnvironmentMutations(project.id);
  const [blockers, setBlockers] = useState<Blocker[]>([]);
  const close = (open: boolean) => {
    if (!open) setBlockers([]);
    onOpenChange(open);
  };
  const onConfirm = async () => {
    if (!env) return;
    setBlockers([]);
    try {
      await update.mutateAsync({ environmentId: env.id, body: { archived: true }, etag: environmentEtag(env, project) });
      toast.success(`${env.slug} archived`, { description: 'Its slug stays reserved; restore it from Archived environments.' });
      close(false);
    } catch (e) {
      const found = blockersOf(e);
      if (found.length) setBlockers(found);
      else showMutationError(e, 'Could not archive the environment');
    }
  };
  return { blockers, close, onConfirm, isPending: update.isPending };
}

/** Archive one environment (`PATCH /environments/{id}` `archived: true`); a 409 lists what still uses it. */
export function ArchiveEnvironmentDialog({ project, env, onOpenChange }: Props) {
  const a = useArchiveEnvironment(project, env, onOpenChange);
  return (
    <ConfirmArchiveDialog
      open={Boolean(env)}
      onOpenChange={a.close}
      testId="env-archive-dialog"
      title={`Archive ${env?.slug ?? 'the environment'}?`}
      description="It leaves the environment lists, the Run build form and every filter. Nothing is deleted."
      impact={archiveImpact(env)}
      confirmLabel="Archive environment"
      busy={a.isPending}
      onConfirm={() => void a.onConfirm()}
    >
      {a.blockers.length ? <BlockersAlert slug={env?.slug} blockers={a.blockers} /> : null}
    </ConfirmArchiveDialog>
  );
}

function archiveImpact(env: Environment | null) {
  return [
    {
      text: (
        <>
          New builds for it are refused: <code className="font-mono text-xs text-foreground">hangar build --env {env?.slug}</code> stops working.
        </>
      ),
    },
    { text: 'Refused while builds are still running on it, or environment variables or build profiles still use it: Hangar lists them.' },
    { text: 'Build history stays; the slug stays reserved and the environment can be restored.' },
  ];
}

function BlockersAlert({ slug, blockers }: { slug: string | undefined; blockers: Blocker[] }) {
  return (
    <Alert variant="destructive" data-testid="env-archive-blockers" className="px-3 py-2.5">
      <Icon name="error" size={18} />
      <AlertTitle className="text-[13px]">{slug} is still in use</AlertTitle>
      <AlertDescription className="text-[13px]">
        <ul className="mt-1 flex flex-col gap-1">
          {blockers.map((b) => (
            <li key={b.kind}>
              <strong>{blockerLabel(b)}</strong>
              {blockerSample(b).length ? <span className="font-mono text-[11px] opacity-90"> · {blockerSample(b).join(', ')}</span> : null}
            </li>
          ))}
        </ul>
        <p className="mt-1.5">Move or archive them first, then archive the environment.</p>
      </AlertDescription>
    </Alert>
  );
}
